import React, { useState, useEffect } from 'react';
import BBALoader from '../BBALoader';
import { Link } from 'react-router-dom';
import { FaBullseye } from 'react-icons/fa';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { getUserGoals } from '../../services/firebase/userGoalsService';
import { getCompanyIdForUser, findAccountIdByEmail, getPrimaryUserIdForAccount } from '../../services/firestoreService';

const formatGoalValue = (value, unit) => {
  if (value === null || value === undefined) return '0';
  if (unit === 'usd') return `$${value.toLocaleString()}`;
  if (unit === 'percent') return `${value}%`;
  return value.toString();
};

// userId scopes the goals to a specific client (CRM Portal View embedding);
// when set, portal-only links are hidden and all goals are shown.
const MarketingGoalsTab = ({ userId = null } = {}) => {
  const { darkMode } = useTheme();
  const { currentUser } = useAuth();
  const [userGoals, setUserGoals] = useState(null);
  const [goalsLoading, setGoalsLoading] = useState(true);
  const [goalsOwnerId, setGoalsOwnerId] = useState(null);

  useEffect(() => {
    if (userId) {
      setGoalsOwnerId(userId);
      return;
    }
    if (!currentUser?.uid) {
      setGoalsOwnerId(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        let ownerId = currentUser.uid;
        let cid = await getCompanyIdForUser(currentUser.uid);
        if (!cid && currentUser.email) {
          cid = await findAccountIdByEmail(currentUser.email, 'Clients').catch(() => null);
        }
        if (cid) {
          const primaryUid = await getPrimaryUserIdForAccount(cid);
          if (primaryUid) ownerId = primaryUid;
        }
        if (!cancelled) setGoalsOwnerId(ownerId);
      } catch (error) {
        console.error('Error resolving marketing goals owner id:', error);
        if (!cancelled) setGoalsOwnerId(currentUser.uid);
      }
    })();
    return () => { cancelled = true; };
  }, [currentUser?.uid, currentUser?.email, userId]);

  useEffect(() => {
    if (!goalsOwnerId) return;
    let cancelled = false;
    (async () => {
      try {
        setGoalsLoading(true);
        const goals = await getUserGoals(goalsOwnerId);
        if (!cancelled) setUserGoals(goals);
      } catch (error) {
        console.error('Error loading user goals:', error);
      } finally {
        if (!cancelled) setGoalsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [goalsOwnerId]);

  return (
    <div className={`bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800 rounded-xl border p-6`}>
      <div className="flex items-center gap-2 mb-4">
        <FaBullseye className="text-xl text-red-600" />
        <h2 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Marketing Goals</h2>
      </div>
      <div className="space-y-4">
        {goalsLoading ? (
          <div className="flex items-center justify-center min-h-[120px]">
            <BBALoader size={32} />
          </div>
        ) : userGoals?.Marketing?.length > 0 ? (
          (userId ? userGoals.Marketing : userGoals.Marketing.slice(0, 3)).map((goal, index) => {
            const progress = goal.target > 0 ? (goal.actual / goal.target) * 100 : 0;
            let currentColor = 'bg-gray-100 dark:bg-gray-900/20 text-gray-800 dark:text-gray-200';
            if (progress >= 100) currentColor = 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-200';
            else if (progress >= 70) currentColor = 'bg-yellow-100 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-200';
            else if (progress > 0) currentColor = 'bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-200';

            return (
              <div key={goal.id || index} className={`p-3 rounded-xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50`}>
                <h3 className={`font-medium text-center mb-2 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                  {goal.metricName}
                </h3>
                <div className="grid grid-cols-2 gap-2">
                  <div className="bg-green-100 dark:bg-green-900/20 p-2 rounded-lg text-center">
                    <div className="text-xs text-green-700 dark:text-green-300 font-medium mb-1">Target</div>
                    <div className="text-sm font-bold text-green-800 dark:text-green-200">
                      {formatGoalValue(goal.target, goal.unit)}
                    </div>
                  </div>
                  <div className={`${currentColor} p-2 rounded-lg text-center`}>
                    <div className="text-xs font-medium mb-1">Current</div>
                    <div className="text-sm font-bold">{formatGoalValue(goal.actual, goal.unit)}</div>
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div className="flex flex-col items-center justify-center min-h-[120px]">
            <FaBullseye className="w-8 h-8 text-gray-400 mb-2" />
            <p className={`text-sm text-center mb-3 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>No marketing goals set yet</p>
            {!userId && (
              <Link to="/portal/goals?from=marketing" className="text-sm text-red-600 hover:text-red-700 font-medium hover:underline">
                Set Your Goals
              </Link>
            )}
          </div>
        )}
      </div>
      {!userId && (
        <div className={`w-full mt-4 pt-4 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <Link
            to="/portal/goals?from=marketing"
            className="w-full px-4 py-2 text-sm text-red-600 hover:text-red-700 font-medium rounded-lg transition-colors hover:bg-red-50 dark:hover:bg-red-900/20 block text-center"
          >
            View All Goals
          </Link>
        </div>
      )}
    </div>
  );
};

export default MarketingGoalsTab;
